import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Clock, Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { format } from "date-fns";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_SLOTS = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM"
];

const ContactPage = () => {
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "",
    preferred_date: null,
    preferred_time: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        const flatServices = [];
        Object.values(response.data).forEach((category) => {
          category.items.forEach((item) => {
            flatServices.push(item);
          });
        });
        setServices(flatServices);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        preferred_date: formData.preferred_date ? format(formData.preferred_date, "yyyy-MM-dd") : null
      };
      
      await axios.post(`${API}/contact`, submitData);
      toast.success("Thank you! We'll contact you soon to confirm your appointment.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_interest: "",
        preferred_date: null,
        preferred_time: "",
        message: ""
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Book Your Appointment"
        description="Schedule your beauty appointment at Glamour Body in Katy, Texas. Contact us for facials, body contouring, waxing, and VIP packages. Email: Glamourbodyhouston@gmail.com. Flexible scheduling available."
        keywords="book appointment Katy Texas, beauty salon booking Houston, schedule facial treatment, body contouring appointment, waxing appointment Katy TX, Glamour Body contact"
        canonical="https://glamourbodyhouston.com/contact"
      />
      
      <div className="pt-24 min-h-screen">
        <section className="relative py-16 px-4" data-testid="contact-section">
          <div className="orb-teal top-10 -left-40" />
          <div className="orb-cyan top-20 -right-40" />
          <div className="orb-green bottom-20 left-20" />
          
          <div className="max-w-4xl mx-auto relative z-10">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-italiana text-4xl sm:text-5xl text-white mb-4 tracking-wider uppercase">Book Your Appointment</h1>
              <p className="text-white/60 text-base lg:text-lg">Ready to transform? Fill out the form below and we'll get back to you shortly.</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="glass-card border-white/10" data-testid="contact-form-card">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Full Name *</label>
                        <Input
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="input-dark rounded-lg"
                          placeholder="Your name"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Email *</label>
                        <Input
                          required
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="input-dark rounded-lg"
                          placeholder="your@email.com"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Phone *</label>
                        <Input
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="input-dark rounded-lg"
                          placeholder="(555) 555-5555"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Service Interest *</label>
                        <Select
                          value={formData.service_interest}
                          onValueChange={(value) => setFormData({ ...formData, service_interest: value })}
                        >
                          <SelectTrigger className="input-dark rounded-lg" data-testid="contact-service-select">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/20">
                            {services.map((service) => (
                              <SelectItem key={service.id} value={service.name} className="text-white hover:bg-white/10">
                                {service.name} - ${service.price}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Preferred Date</label>
                        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                          <PopoverTrigger asChild>
                            <Button 
                              variant="outline" 
                              className="w-full input-dark rounded-lg justify-start text-left font-normal"
                              data-testid="contact-date-btn"
                            >
                              <Calendar className="mr-2 h-4 w-4 text-cyan-400" />
                              {formData.preferred_date ? format(formData.preferred_date, "PPP") : "Select a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-zinc-900 border-white/20" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={formData.preferred_date}
                              onSelect={(date) => {
                                setFormData({ ...formData, preferred_date: date });
                                setCalendarOpen(false);
                              }}
                              disabled={(date) => date < new Date()}
                              className="bg-zinc-900 text-white"
                              data-testid="contact-calendar"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Preferred Time</label>
                        <Select
                          value={formData.preferred_time}
                          onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                        >
                          <SelectTrigger className="input-dark rounded-lg" data-testid="contact-time-select">
                            <Clock className="mr-2 h-4 w-4 text-cyan-400" />
                            <SelectValue placeholder="Select a time" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/20">
                            {TIME_SLOTS.map((time) => (
                              <SelectItem key={time} value={time} className="text-white hover:bg-white/10">
                                {time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Message (Optional)</label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="input-dark rounded-lg min-h-24"
                        placeholder="Tell us about your goals or any questions you have..."
                        data-testid="contact-message-input"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full btn-primary rounded-full py-6 text-lg"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Request Appointment"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div 
              className="mt-12 grid md:grid-cols-3 gap-6 text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="glass-card rounded-xl p-6">
                <Mail className="mx-auto mb-3 text-cyan-400" size={24} />
                <p className="text-white/70 text-sm">Email Us</p>
                <a href="mailto:Glamourbodyhouston@gmail.com" className="text-white hover:text-cyan-400 transition-colors text-sm">
                  Glamourbodyhouston@gmail.com
                </a>
              </div>
              <div className="glass-card rounded-xl p-6">
                <MapPin className="mx-auto mb-3 text-cyan-400" size={24} />
                <p className="text-white/70 text-sm">Location</p>
                <p className="text-white text-sm">Katy, Texas</p>
                <p className="text-white/60 text-xs">Greater Houston Area</p>
              </div>
              <div className="glass-card rounded-xl p-6">
                <Clock className="mx-auto mb-3 text-cyan-400" size={24} />
                <p className="text-white/70 text-sm">Hours</p>
                <p className="text-white text-sm">By Appointment</p>
                <p className="text-white/60 text-xs">Flexible scheduling available</p>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
